import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";
import "./Profile.css";

// Backend sends ISO date strings (Dodo) or Unix seconds (legacy)
function formatSubscriptionDate(value) {
  if (value == null) return "—";
  const date = typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
}

function ProfilePage() {
  document.title = `Profile | LeadZLab`;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [redditProfileId, setRedditProfileId] = useState("");
  const [plan, setPlan] = useState("");
  const [subscriptionStartedAt, setSubscriptionStartedAt] = useState("");
  const [subscriptionActiveTill, setSubscriptionActiveTill] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("canceled");

  // * Email marketing
  const [promotionMail, setPromotionMail] = useState(false);
  const [btsMails, setBtsMails] = useState(false);
  const [productUpdates, setProductUpdates] = useState(false);
  const [feedbackEmails, setFeedbackEmails] = useState(false);
  const [newsletter, setNewsletter] = useState(false);

  // * Scout profile (used for "Create scout from my profile")
  const [profileWhatIDo, setProfileWhatIDo] = useState("");
  const [profileLookingFor, setProfileLookingFor] = useState("");
  const [profilePortfolioUrl, setProfilePortfolioUrl] = useState("");
  const [profileExtra, setProfileExtra] = useState("");
  const [outreachMessages, setOutreachMessages] = useState(["", "", ""]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("account");

  // * Update email preferences
  const handleUpdateEmailPreferences = async () => {
    try {
      // Send email preference updates to the backend
      await axios.put(
        `${environment.BACKEND}/user/email-preferences`,
        {
          promotionMail,
          btsMails,
          productUpdates,
          feedbackEmails,
          newsletter,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      toast.success(`Email preferences updated successfully`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    }
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleRedditProfileIdChange = (e) => {
    setRedditProfileId(e.target.value);
  };

  // * Handle profile update
  const handleUpdateProfile = async () => {
    try {
      await axios.put(
        `${environment.BACKEND}/user`,
        {
          firstName: firstName,
          lastName: lastName,
          redditUsername: redditProfileId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      toast.success(`Profile updated successfully`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    }
  };

  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const handleCancelSubscription = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancelSubscription = async () => {
    try {
      await axios.post(
        `${environment.BACKEND}/user/payment/cancel`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      toast.success(`Subscription cancelled successfully`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
      setShowCancelConfirmation(false);
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    }
  };

  const handleCancelConfirmationCancel = () => {
    setShowCancelConfirmation(false);
  };

  const handleSaveScoutProfile = async () => {
    setProfileSaving(true);
    try {
      await axios.put(
        `${environment.BACKEND}/user/onboarding/profile`,
        {
          profileWhatIDo: profileWhatIDo.trim(),
          profileLookingFor: profileLookingFor.trim(),
          profilePortfolioUrl: profilePortfolioUrl.trim(),
          profileExtra: profileExtra.trim(),
          outreachMessages: outreachMessages.map((m) => m.trim()).filter(Boolean),
        },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      toast.success("Scout profile updated", {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message ?? err.message, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const { user } = useAuthContext();

  // * Get user
  const getUser = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      const userData = data.data;
      setFirstName(userData.firstName);
      setLastName(userData.lastName);
      setRedditProfileId(userData.redditUsername);
      setPlan(userData.plan);
      setPromotionMail(userData.emailPreferences?.promotions ?? false);
      setBtsMails(userData.emailPreferences?.bts ?? false);
      setProductUpdates(userData.emailPreferences?.productUpdates ?? false);
      setFeedbackEmails(userData.emailPreferences?.feedback ?? false);
      setNewsletter(userData.emailPreferences?.newsletter ?? false);
      setProfileWhatIDo(userData.profileWhatIDo ?? "");
      setProfileLookingFor(userData.profileLookingFor ?? "");
      setProfilePortfolioUrl(userData.profilePortfolioUrl ?? "");
      setProfileExtra(userData.profileExtra ?? "");
      setOutreachMessages(Array.isArray(userData.outreachMessages) && userData.outreachMessages.length
        ? [...userData.outreachMessages.slice(0, 5)]
        : ["", "", ""]);
    } catch (err) {
      console.log(err.response.data);
    }
  };

  // * Get subscription
  const getSubscriptionStatus = async () => {
    try {
      const { data } = await axios.get(
        `${environment.BACKEND}/user/payment/subscription`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      const subscriptionData = data.subscription;
      if (subscriptionData) {
        setSubscriptionStartedAt(subscriptionData.subscriptionStartedAt);
        setSubscriptionActiveTill(subscriptionData.subscriptionActiveTill);
        setSubscriptionStatus(subscriptionData.subscriptionStatus ?? "unknown");
      }
    } catch (err) {
      console.log(err.response.data);
    }
  };

  useEffect(() => {
    getUser();
    getSubscriptionStatus();
  }, []);

  const tabs = [
    { id: "account", label: "Account" },
    { id: "scout", label: "Scout profile" },
    { id: "preferences", label: "Preferences" },
  ];

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">Profile</h1>
        <p className="mt-1 text-slate-600">Your account and preferences.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-6" aria-label="Profile sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab panels */}
      {activeTab === "account" && (
        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Your details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="firstNameInput">First name</label>
                <input
                  id="firstNameInput"
                  type="text"
                  className="input-base w-full"
                  value={firstName}
                  onChange={handleFirstNameChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="lastNameInput">Last name</label>
                <input
                  id="lastNameInput"
                  type="text"
                  className="input-base w-full"
                  value={lastName}
                  onChange={handleLastNameChange}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="redditProfileIdInput">Reddit username</label>
              <input
                id="redditProfileIdInput"
                type="text"
                className="input-base w-full"
                value={redditProfileId}
                onChange={handleRedditProfileIdChange}
              />
            </div>
            <button type="button" className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700" onClick={handleUpdateProfile}>
              Save changes
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900">Plan</h2>
            <p className="mt-1 text-slate-600">
              <span className="font-medium capitalize">{plan || "—"}</span>
            </p>
            {subscriptionStartedAt && (
              <p className="mt-1 text-sm text-slate-500">
                Started {formatSubscriptionDate(subscriptionStartedAt)}
              </p>
            )}
            {subscriptionActiveTill && (
              <p className="mt-0.5 text-sm text-slate-500">
                Active until {formatSubscriptionDate(subscriptionActiveTill)}
              </p>
            )}
            {showCancelConfirmation ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <p className="text-sm text-slate-600">Cancel your subscription?</p>
                <button type="button" className="btn-primary bg-red-600 hover:bg-red-700" onClick={handleConfirmCancelSubscription}>
                  Yes, cancel
                </button>
                <button type="button" className="btn-secondary" onClick={handleCancelConfirmationCancel}>
                  Keep it
                </button>
              </div>
            ) : subscriptionActiveTill && subscriptionStatus !== "canceled" && subscriptionStatus !== "cancelled" && (
              <button
                type="button"
                className="mt-4 text-sm font-medium text-red-600 hover:text-red-700"
                onClick={handleCancelSubscription}
              >
                Cancel subscription
              </button>
            )}
            {(subscriptionStatus === "canceled" || subscriptionStatus === "cancelled") && (
              <p className="mt-2 text-sm font-medium text-amber-700">Subscription cancelled</p>
            )}
          </div>
        </div>
      )}

      {activeTab === "scout" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Scout profile</h2>
          <p className="text-sm text-slate-600">Used when you create a scout with “Create from my profile” so we can suggest better keywords and places.</p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">What do you do?</label>
            <textarea
              value={profileWhatIDo}
              onChange={(e) => setProfileWhatIDo(e.target.value)}
              placeholder="e.g. I design brand identities and marketing assets for startups"
              rows={3}
              className="input-base w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">What are you looking for?</label>
            <textarea
              value={profileLookingFor}
              onChange={(e) => setProfileLookingFor(e.target.value)}
              placeholder="e.g. Clients posting “looking for a designer” or “need a logo”"
              rows={3}
              className="input-base w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Portfolio or website (optional)</label>
            <input
              type="url"
              value={profilePortfolioUrl}
              onChange={(e) => setProfilePortfolioUrl(e.target.value)}
              placeholder="https://..."
              className="input-base w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Anything else we should know? (optional)</label>
            <textarea
              value={profileExtra}
              onChange={(e) => setProfileExtra(e.target.value)}
              placeholder="e.g. I prefer B2B, or I focus on SaaS"
              rows={2}
              className="input-base w-full"
            />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-medium text-slate-800">Outreach message examples (optional)</p>
            <p className="mt-0.5 text-xs text-slate-500">Stored for later; we’ll use these in future features.</p>
            {[0, 1, 2, 3, 4].map((i) => (
              <input
                key={i}
                type="text"
                value={outreachMessages[i] ?? ""}
                onChange={(e) => {
                  const next = [...outreachMessages];
                  while (next.length <= i) next.push("");
                  next[i] = e.target.value;
                  setOutreachMessages(next.slice(0, 5));
                }}
                placeholder={`Example ${i + 1}`}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            ))}
          </div>
          <button
            type="button"
            className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            onClick={handleSaveScoutProfile}
            disabled={profileSaving}
          >
            {profileSaving ? "Saving…" : "Save scout profile"}
          </button>
        </div>
      )}

      {activeTab === "preferences" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Email preferences</h2>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={promotionMail}
                onChange={(e) => setPromotionMail(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Promotions (offers, discounts, freebies)</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={btsMails}
                onChange={(e) => setBtsMails(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Behind the scenes</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={productUpdates}
                onChange={(e) => setProductUpdates(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Product updates</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={feedbackEmails}
                onChange={(e) => setFeedbackEmails(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Feedback surveys</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Newsletter</span>
            </label>
          </div>
          <button type="button" className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700" onClick={handleUpdateEmailPreferences}>
            Update email preferences
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
